import PropTypes from 'prop-types';
import React from 'react';

import { useAjaxListing } from '$hooks/ajax-listing';
import { useLocale } from '$contexts/locale';

import { AjaxListingContext, AjaxListingPagination } from '$partials/base/ajax-listing';
import { ArticleStandardListingItem } from '$partials/listings/items';
import { Button } from '$partials/ui';
import { Footer, Header, Meta } from '$partials/global';
import ModulePopular from '$partials/modules/individual/popular';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

const SubnavLink = ({
    url: originalURL,
    label,
    pageType
}) => {
    return (
        <li className='group inline-block relative'>
            <Button
                as='a'
                className='mt-5 sm:mt-0 flex items-center hover:bg-orange hover:text-white bg-white text-orange  rounded type-style-7 px-20 py-15 mr-5'
                href={`${pageType === 'BvaMagazineSportPage' ? '/activities/' : pageType === 'BvaMagazineTopicPage' ? '/topics/' : pageType === 'BvaMagazineGenericPage' ? '/page/' : ''}${originalURL}`}
                type='button'>
                {label}
            </Button>
        </li>
    );
};

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const LandingPage = ({ data, domain }) => {
    const page = data?.page;
    const { current: locale, translate: t } = useLocale();

    let activities = [];
    let topics = [];

    page?.subnavigations?.items.forEach(subnav => {
        if(!subnav) { return null; }
        if (subnav.__typename === 'BvaMagazineSportPage') {
            activities.push(subnav.slug);
        }
        else if (subnav.__typename === 'BvaMagazineTopicPage') {
            topics.push(subnav.slug);
        }
    });

    const listing = useAjaxListing({
        items: data?.items?.items,
        page: data?.items?.page || 1,
        path: '/api/v1/listings/articles',
        totalItems: data?.items?.total,
        totalPages: data?.items?.totalPages,
        query: { locale: locale?.code, activities: activities, topics: topics, offset: 3  }
    });
    if (!page) { return null; }

    return (
        <>
            <Meta page={page} />

            <main
                className='bg-grey min-h-screen'
                id='main-content'>
                <div className='layout-container'>
                    <Header
                        menu={data?.navigation}
                        search={data?.search}/>

                    <article className={`pt-60 ${page.marketingBanner?.title ? '' : 'sm:pt-75 md:pt-80'}`}>
                        {/* Marketing Banner */}
                        {page.marketingBanner?.title && (
                            <div className='px-10 sm:px-20 mb-60'>
                                <div className='w-full bg-peach pt-15 pb-20 sm:py-20 px-20 rounded type-style-8 flex flex-wrap items-center'>
                                    <p className='w-full sm:w-auto '>{page.marketingBanner.title}</p>
                                    {page.marketingBanner?.ctaLink && (
                                        <Button
                                            as='a'
                                            className='mt-5 sm:mt-0 flex items-center bg-orange text-white  rounded type-style-7 px-20 py-15 sm:ml-20'
                                            href={page.marketingBanner.ctaLink}
                                            type='button'>
                                            {page.marketingBanner?.ctaLabel || 'Learn More'}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Header */}
                        <header className='px-10 sm:px-20'>
                            <div className='md:pr-100'>
                                <h1 className='type-style-2 inline mr-10'>
                                    {page.heading || `${t('Latest')} ${page.title} ${t('Stories')}`}
                                </h1>
                            </div>
                            {page.subnavigations?.items.length > 0 && (
                                <div className='type-style-7 mt-40 mb-70'>
                                    <ul className='list-none'>
                                        {page.subnavigations?.items?.map(subnav =>
                                            subnav ? (
                                                <SubnavLink
                                                    key={subnav.slug}
                                                    label={subnav.title}
                                                    pageType={subnav.__typename}
                                                    url={subnav.slug} />
                                            ) : null
                                        )}
                                    </ul>
                                </div>
                            )}
                        </header>

                        {/* Latest 3 articles */}
                        <section
                            aria-label='Latest Articles'
                            className='flex flex-wrap mt-40'>
                            {data?.items?.latest?.items?.map((item, i) => {
                                if (!item) {return null;}
                                return (
                                    <article
                                        aria-label={item.title}
                                        aria-posinset={i + 1}
                                        aria-setsize={listing.totalItems}
                                        className={'flex flex-col w-full sm:w-4/12 pb-50 md:pb-70 px-10 sm:px-20'}
                                        key={i}
                                        tabIndex={-1}>
                                        <ArticleStandardListingItem {...item} />
                                    </article>
                                );
                            })}
                        </section>

                        {/* Popular */}
                        {page.popular && (
                            <ModulePopular
                                articles={data?.popular}
                                section={page.popular}/>
                        )}

                        {/* Listing */}
                        <AjaxListingContext.Provider value={listing}>
                            <section
                                aria-busy={listing.loading}
                                aria-label='Articles'
                                className='flex flex-wrap mt-40'
                                role='feed'>
                                {listing?.items?.length > 0 && (
                                    <div className='w-full px-10 sm:px-20 mb-30'>
                                        <h2 className='type-style-2 inline mr-10'>
                                            {t('More Stories')}
                                        </h2>
                                    </div>
                                )}
                                {listing?.items?.map((item, i) => {
                                    if (!item) {return null;}
                                    return (
                                        <article
                                            aria-label={item.title}
                                            aria-posinset={i + 1}
                                            aria-setsize={listing.totalItems}
                                            className={'flex flex-col w-full sm:w-6/12 md:w-4/12 pb-50 md:pb-70 px-10 sm:px-20'}
                                            key={`${item.sys?.id}-${i}`}
                                            tabIndex={-1}>
                                            <ArticleStandardListingItem {...item} />
                                        </article>
                                    );
                                })}

                                <div className='border-b border-dark-grey mx-10 sm:mx-20 w-full' />
                            </section>
                            {listing.totalPages > 1 && (
                                <div className='w-full px-10 sm:px-20 mt-30 mb-20 sm:mb-0 flex items-start'>
                                    <AjaxListingPagination
                                        current={listing.page}
                                        totalPages={listing.totalPages}/>
                                </div>
                            )}
                        </AjaxListingContext.Provider>
                    </article>

                    <Footer
                        domain={domain}
                        menu={data?.footer}/>
                </div>
            </main>
        </>
    );
};

/* =========================================================================  */
/* Typing and exports */
/* =========================================================================  */

SubnavLink.propTypes = {
    url: PropTypes.string,
    label: PropTypes.string,
    pageType: PropTypes.string,
};

const propTypes = {
    data: PropTypes.shape({
        items: PropTypes.shape({
            items: PropTypes.array,
            latest: PropTypes.object,
            page: PropTypes.number,
            total: PropTypes.number,
            totalPages: PropTypes.number,
        }),
        popular: PropTypes.shape({
            items: PropTypes.array,
        }),
        page: PropTypes.shape({
            title: PropTypes.string,
            modules: PropTypes.shape({
                items: PropTypes.array
            })
        }),
        footer: PropTypes.shape({
            items: PropTypes.array
        }),
        navigation: PropTypes.object,
        search: PropTypes.object,
    }),
    domain: PropTypes.string,
};

LandingPage.propTypes = propTypes;

export default LandingPage;
